import { useMemo, useState } from "react";
import { Check, Copy, Lock, Search } from "lucide-react";
import { DsAlert, DsButton, DsIcon, DsInput } from "../../../design-system";
import { moduleSearchHits, moduleSections, moduleStats } from "./roleAccessUtils";

export default function ModulesStep({
  catalog,
  access,
  selectedModules,
  lockedModules,
  readOnly,
  error,
  copySources,
  onToggleModule,
  onSelectAll,
  onClearAll,
  onCopyFrom,
}) {
  const [query, setQuery] = useState("");
  const [copyId, setCopyId] = useState("");

  const cards = useMemo(
    () =>
      catalog
        .map((mod) => ({ mod, hits: moduleSearchHits(mod, query) }))
        .filter((c) => c.hits.matches),
    [catalog, query]
  );

  return (
    <div className="space-y-4">
      {error ? <DsAlert tone="danger">{error}</DsAlert> : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative min-w-[220px] flex-1">
          <DsIcon
            icon={Search}
            size="sm"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
          />
          <DsInput
            type="search"
            aria-label="Search modules or screens"
            placeholder="Search a module or screen, e.g. Payslips, Leave Requests"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        {!readOnly ? (
          <>
            <DsButton variant="outline" size="sm" onClick={onSelectAll}>
              Select all
            </DsButton>
            <DsButton variant="outline" size="sm" onClick={onClearAll}>
              Clear all
            </DsButton>
          </>
        ) : null}
      </div>

      {!readOnly && copySources.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-[hsl(var(--ds-border))] px-3 py-2.5">
          <DsIcon icon={Copy} size="sm" className="text-[hsl(var(--ds-muted-fg))]" />
          <label htmlFor="urm-copy-from" className="text-sm text-[hsl(var(--ds-fg))]">
            Start from another role&apos;s permissions:
          </label>
          <select
            id="urm-copy-from"
            value={copyId}
            onChange={(e) => setCopyId(e.target.value)}
            className="h-8 min-w-[180px] rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-sm text-[hsl(var(--ds-fg))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          >
            <option value="">Choose a role…</option>
            {copySources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <DsButton size="sm" disabled={!copyId} onClick={() => onCopyFrom(copyId)}>
            Copy
          </DsButton>
          <span className="w-full text-[11px] text-[hsl(var(--ds-muted-fg))]">
            Copies modules and screen permissions. You can still change them in the next step. Data
            access is set separately.
          </span>
        </div>
      ) : null}

      <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
        Pick the areas this role works in. You choose exact screens and actions in the next step.
        Removing a module clears its permissions. {selectedModules.size} of {catalog.length} selected.
      </p>

      {cards.length === 0 ? (
        <p className="ds-body py-8 text-center text-[hsl(var(--ds-muted-fg))]">No module or screen matches “{query}”.</p>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {cards.map(({ mod, hits }) => {
            const selected = selectedModules.has(mod.key);
            const locked = lockedModules.has(mod.key);
            const stats = moduleStats(mod, access?.[mod.key]);
            const sections = moduleSections(mod);
            return (
              <li key={mod.key}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={selected}
                  aria-disabled={locked || readOnly}
                  onClick={() => !locked && !readOnly && onToggleModule(mod.key)}
                  className={`group flex h-full w-full flex-col rounded-xl border p-3.5 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] motion-reduce:transition-none ${
                    selected
                      ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.06)]"
                      : "border-[hsl(var(--ds-border))] hover:border-[hsl(var(--ds-primary)/0.45)]"
                  } ${locked || readOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                >
                  <span className="flex items-start gap-2.5">
                    <span
                      aria-hidden
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                        selected
                          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                      }`}
                    >
                      {locked ? <DsIcon icon={Lock} size="sm" className="h-3 w-3" /> : selected ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-[hsl(var(--ds-fg))]">{mod.label}</span>
                      {mod.description ? (
                        <span className="mt-0.5 line-clamp-2 block text-xs text-[hsl(var(--ds-muted-fg))]">
                          {mod.description}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span className="mt-2.5 block pl-7 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    {mod.screens.length} screen{mod.screens.length === 1 ? "" : "s"}
                    {sections.length ? ` · ${sections.join(" · ")}` : ""}
                  </span>
                  {selected && stats.granted > 0 ? (
                    <span className="mt-1 block pl-7 text-[11px] font-medium text-[hsl(var(--ds-primary))]">
                      {stats.screensOn} screen{stats.screensOn === 1 ? "" : "s"} with permissions
                    </span>
                  ) : null}
                  {query && hits.screens.length ? (
                    <span className="mt-1 block pl-7 text-[11px] text-[hsl(var(--ds-fg))]">
                      Matches: {hits.screens.slice(0, 4).map((s) => s.label).join(", ")}
                      {hits.screens.length > 4 ? ` +${hits.screens.length - 4} more` : ""}
                    </span>
                  ) : null}
                  {locked ? (
                    <span className="mt-1 block pl-7 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      Not in the modules you can grant{selected ? " — kept unchanged" : ""}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
